import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { dashboardService } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const TeacherDashboard: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { school } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardService.getStats();
        setStats(response.data.stats);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Layout title="Teacher Dashboard">
        <div className="text-center py-8">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Teacher Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* My Classes */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="flex-1">
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>My Classes</p>
              <p className="text-3xl font-bold text-blue-600">{stats?.myClasses || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </div>

        {/* My Subjects */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="flex-1">
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>My Subjects</p>
              <p className="text-3xl font-bold text-green-600">{stats?.mySubjects || 0}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </div>
        </div>

        {/* Pending Evaluations */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="flex-1">
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pending Evaluations</p>
              <p className="text-3xl font-bold text-orange-600">{stats?.pendingEvaluations || 0}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Quick Actions</h3>
          <div className="space-y-2">
            <button 
              onClick={() => navigate('/teacher/attendance')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-left"
            >
              ✓ Mark Attendance
            </button>
            <button 
              onClick={() => navigate('/teacher/results')}
              className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 text-left"
            >
              📊 Upload Results
            </button>
            <button 
              onClick={() => navigate('/teacher/assignments')}
              className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 text-left"
            >
              📝 Create Assignment
            </button>
            <button 
              onClick={() => navigate('/teacher/notices')}
              className="w-full bg-yellow-600 text-white py-2 px-4 rounded hover:bg-yellow-700 text-left"
            >
              📢 Post Notice
            </button>
          </div>
        </div>

        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 md:col-span-2`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Today's Schedule</h3>
          <div className="space-y-3">
            <div className="flex items-center border-l-4 border-blue-500 pl-4 py-2">
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Class 10-A - Mathematics</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Period 1 • 9:00 AM - 10:00 AM • Room 201</p>
              </div>
            </div>
            <div className="flex items-center border-l-4 border-green-500 pl-4 py-2">
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Class 9-B - Mathematics</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Period 3 • 11:00 AM - 12:00 PM • Room 203</p>
              </div>
            </div>
            <div className="flex items-center border-l-4 border-purple-500 pl-4 py-2">
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Class 10-B - Mathematics</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Period 5 • 1:00 PM - 2:00 PM • Room 201</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className={`mt-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Activity</h3>
        <div className="space-y-3">
          <div className="border-l-4 border-blue-500 pl-4 py-2">
            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Attendance marked for Class 10-A</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>2 hours ago • 35/40 students present</p>
          </div>
          <div className="border-l-4 border-green-500 pl-4 py-2">
            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Assignment created: Quadratic Equations</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>5 hours ago • Due date: December 20th</p>
          </div>
          <div className="border-l-4 border-purple-500 pl-4 py-2">
            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notice posted: Exam Schedule</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>1 day ago • Viewed by 142 students</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeacherDashboard;
